/**
 * Browser composition root: builds the AppController with the real network, storage,
 * voice and PWA services, and wires the services that only react to state.
 */
import { computed, effect } from '@preact/signals';
import { installAudioUnlock } from '../audio/context';
import { ru } from '../i18n/ru';
import { createAuthApi } from '../net/api';
import { browserLifecycle } from '../net/lifecycle';
import { NetClient } from '../net/NetClient';
import { webSocketUrl } from '../net/urls';
import { createInstallPrompt } from '../pwa/install';
import { browserStorageManager, keepStorage } from '../pwa/persistentStorage';
import { reloadWhenSafe } from '../pwa/reloadWhenSafe';
import { createUpdater } from '../pwa/serviceWorker';
import { ScreenWakeLock } from '../pwa/wakeLock';
import { calendarSeason, seasonOverride } from '../season/season';
import { openStorage } from '../state/storage';
import { blockPinchZoom, syncSeasonAttribute } from '../ui/dom';
import { createVoiceController } from '../voice/createVoiceController';
import { AppController } from './AppController';
import { backButton } from './backButton';
import { installPadNavigation } from './padNavigation';
import type { ScreenId } from './screen';

/** A pending update may reload the page only on these screens (never mid-race or mid-edit). */
const SAFE_TO_RELOAD: ReadonlySet<ScreenId> = new Set([
  'login',
  'room',
  'results',
  'stats',
  'replaced',
  'update',
]);
/** Time to read the "updating" toast before the reload. */
const UPDATE_NOTICE_MS = 1500;
/** How often an open app looks at the calendar again (holidays begin and end at midnight). */
const SEASON_CHECK_MS = 60 * 60 * 1000;

export function createApp(): AppController {
  // Sound may start only after a user gesture: the first tap or key anywhere unlocks the
  // AudioContext shared by the game audio and the voice chat (and again after iOS
  // interruptions), so a race that starts on a server message is not silent.
  installAudioUnlock();
  // Android's Back closes dialogs and the in-game menu instead of leaving the app.
  backButton.install(window);
  blockPinchZoom(document);
  // The dev override (`?season=halloween|winter|none`) stays in the address all session long.
  const override = seasonOverride(window.location.search);

  const app = new AppController({
    storage: openStorage(),
    api: createAuthApi(),
    createNet: (hello) =>
      new NetClient({
        url: () => webSocketUrl(window.location),
        hello,
        lifecycle: browserLifecycle(),
      }),
    createVoice: createVoiceController,
    install: createInstallPrompt(window),
    updater: createUpdater(),
    season: () => calendarSeason(new Date(), override),
  });

  // Holidays: the shell's holiday styles follow <html data-season> (its decorations and the
  // engine get the season from the settings). The calendar is looked at again now and then.
  syncSeasonAttribute(document.documentElement, app.settings.season);
  setInterval(() => app.refreshSeason(), SEASON_CHECK_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') app.refreshSeason();
  });

  // Gamepads: the focus, A and B outside the race, a toast at the first press (the game
  // screen reads the pads itself).
  installPadNavigation(app.toasts);

  const wakeLock = new ScreenWakeLock();
  effect(() => wakeLock.setWanted(app.room.room.value !== null));

  // Logged in: the device key matters from now on — ask the browser to keep the storage.
  let storageKept = false;
  effect(() => {
    if (app.session.auth.value === null || storageKept) return;
    storageKept = true;
    void keepStorage(browserStorageManager());
  });

  reloadWhenSafe({
    updateReady: app.updater.updateReady,
    safe: computed(() => SAFE_TO_RELOAD.has(app.screen.value) && app.ui.dialog.value === null),
    announce: () => {
      const id = app.toasts.show(ru.update.applying, { durationMs: UPDATE_NOTICE_MS + 1000 });
      return () => app.toasts.dismiss(id);
    },
    reload: () => app.updater.applyUpdate(),
    noticeMs: UPDATE_NOTICE_MS,
  });

  return app;
}
